/**
 * ContinuityAssetCard — one investment shown the way the Vault reads it.
 *
 * Renders the four core questions ("What / Where / How much / Paperwork")
 * plus a soft status label and an "Edit continuity details" button.
 */
import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PenSquare, PenLine, Paperclip } from 'lucide-react';
import { computeContinuityStatus, holderFromInvestment } from './continuityStatus';

const money = (v, ccy) => {
  if (v === null || v === undefined) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy || 'USD', maximumFractionDigits: 0 }).format(v);
  } catch (_) {
    return `${ccy || ''} ${Number(v).toLocaleString()}`;
  }
};

/** Small amber "pen + Add {label}" nudge shown in place of a missing value.
 *  Clicking it opens the same Edit modal as the primary CTA button below,
 *  so the founder can fill the missing detail in one tap. */
function AddNudge({ label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 transition"
    >
      <PenLine className="w-3 h-3" />
      <span>Add {label}</span>
    </button>
  );
}

function TypeSpecificExtras({ inv }) {
  const t = inv.type;
  const rows = [];
  if (t === 'real_estate') {
    rows.push(['Address', [inv.property_street_address, inv.property_city, inv.property_country].filter(Boolean).join(', ') || null]);
    if (inv.property_type) rows.push(['Property type', inv.property_type]);
  } else if (t === 'crypto') {
    if (inv.wallet_address) rows.push(['Wallet', inv.wallet_address]);
  } else if (t === 'gold') {
    if (inv.gold_storage_location) rows.push(['Where stored', inv.gold_storage_location]);
    if (inv.gold_weight_oz) rows.push(['Weight (oz)', inv.gold_weight_oz]);
  } else if (t === 'quoted_stocks' || t === 'etf') {
    if (inv.ticker_symbol) rows.push(['Ticker', inv.ticker_symbol]);
    if (inv.stock_exchange) rows.push(['Exchange', inv.stock_exchange]);
  } else if (t === 'government_bond' || t === 'corporate_bond' || t === 'treasury_bill' || t === 'fixed_deposit') {
    if (inv.issuer_name) rows.push(['Issuer', inv.issuer_name]);
    if (inv.maturity_date) rows.push(['Matures', new Date(inv.maturity_date).toLocaleDateString()]);
  } else if (t === 'private_equity' || t === 'mutual_fund') {
    if (inv.fund_manager) rows.push(['Fund manager', inv.fund_manager]);
  }
  if (rows.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600 pt-2 border-t border-slate-100 mt-2">
      {rows.map(([k, v]) => (
        <div key={k}><span className="text-slate-400">{k}:</span> <span className="text-slate-700" data-sensitive="identifier">{v}</span></div>
      ))}
    </div>
  );
}

export function ContinuityAssetCard({ investment, onEdit, docCount = 0 }) {
  const status = computeContinuityStatus(investment);
  const value = investment.display_value ?? investment.current_value ?? investment.amount;
  const ccy = investment.display_currency || investment.currency;
  const holder = holderFromInvestment(investment);
  const heldLabel = holder.name
    ? (holder.role ? `${holder.name} · ${holder.role}` : holder.name)
    : investment.institution;

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-white" data-testid={`ca-card-${investment.investment_id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="font-medium text-slate-800" data-sensitive="asset-name">{investment.name}</div>
          <div className="text-xs text-slate-500">
            <span>{investment.type.replaceAll('_', ' ')}</span>
            {/* Disambiguation line — when the user has multiple positions in the
                same holding (e.g. three separate Vodafone lots), the cards
                would otherwise look like duplicates. Show whichever of these
                distinguishing markers we have, in preference order. */}
            {(() => {
              const acquired = investment.acquisition_date || investment.purchase_date || investment.purchased_at;
              const acqLabel = acquired ? new Date(acquired).toLocaleDateString() : null;
              const bits = [
                investment.ticker_symbol,
                acqLabel && `acquired ${acqLabel}`,
                investment.units && `${investment.units} units`,
                investment.account_number && `acct ${String(investment.account_number).slice(-4)}`,
                investment.reference_note,
              ].filter(Boolean);
              return bits.length ? (
                <span className="ml-1 text-slate-400" data-testid={`ca-disambig-${investment.investment_id}`} data-sensitive="identifier">
                  &middot; {bits.slice(0, 2).join(' · ')}
                </span>
              ) : null;
            })()}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {docCount > 0 && (
            <span className="inline-flex items-center gap-1 text-xs text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5" data-testid={`ca-docs-${investment.investment_id}`}>
              <Paperclip className="w-3 h-3" /> {docCount}
            </span>
          )}
          <Badge className={`border text-xs ${status.className}`} data-testid={`ca-status-${investment.investment_id}`}>{status.label}</Badge>
        </div>
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div>
          <dt className="text-slate-400">Where is it held</dt>
          <dd className="text-slate-700">
            {heldLabel && String(heldLabel).trim() ? (
              <>
                <span data-sensitive="identifier">{heldLabel}</span>
                {investment.country ? <span className="text-slate-400"> · <span data-sensitive="identifier">{investment.country}</span></span> : null}
              </>
            ) : (
              <AddNudge
                label="Where it's held"
                onClick={() => onEdit(investment)}
                testid={`ca-nudge-${investment.investment_id}-held`}
              />
            )}
          </dd>
        </div>
        <div>
          <dt className="text-slate-400">How much</dt>
          <dd className="text-slate-700" data-sensitive="monetary">{money(value, ccy)}</dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-slate-400">Where&rsquo;s the paperwork</dt>
          <dd className="text-slate-700">
            {investment.continuity_paperwork_location && String(investment.continuity_paperwork_location).trim() ? (
              <span data-sensitive="identifier">{investment.continuity_paperwork_location}</span>
            ) : (
              <AddNudge
                label="Where the paperwork lives"
                onClick={() => onEdit(investment)}
                testid={`ca-nudge-${investment.investment_id}-paperwork`}
              />
            )}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-slate-400">Access instructions</dt>
          <dd className="text-slate-700">
            {investment.continuity_access_instructions && String(investment.continuity_access_instructions).trim() ? (
              <span className="whitespace-pre-line" data-sensitive="identifier">{investment.continuity_access_instructions}</span>
            ) : (
              <AddNudge
                label="Access instructions"
                onClick={() => onEdit(investment)}
                testid={`ca-nudge-${investment.investment_id}-access`}
              />
            )}
          </dd>
        </div>
      </dl>

      <TypeSpecificExtras inv={investment} />

      <div className="mt-3 flex justify-end">
        <Button size="sm" variant="outline" onClick={() => onEdit(investment)} data-testid={`ca-edit-${investment.investment_id}`}>
          <PenSquare className="w-3.5 h-3.5 mr-1.5" /> Edit continuity details
        </Button>
      </div>
    </div>
  );
}
